import React, { useRef, useEffect, useMemo } from "react";

import { Routes, Route, Navigate } from "react-router-dom";

import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

import { createTheme, ThemeProvider } from "@mui/material/styles";

import { useTranslation } from "react-i18next";

import * as locales from "@mui/material/locale";
import { createAppTheme } from "./theme";

import { ROUTES, ROUTES_LIST } from "./home/constants/routes";
import NotFound from "./NotFound";
import { FLayout } from "./home/components";
import { useAppStore } from "./app/zustandStore";
import { queryClient } from "./app/queryClient";
import { SecureFDashboard } from "./home/pages";
import { SecurePortal } from "./home/pages/Portal";
import NewRegister from "./NewRegister";
import RegisterError from "./RegisterError";
import FRegisterConfirm from "./RegisterConfirm";
import FIntro from "./Intro";
import { AuthRoute } from "./common/components/AuthRoute";

export const App = () => {
  const { t } = useTranslation();
  const currentLanguage = useAppStore((state) => state.currentLanguage);
  const themeMode = useAppStore((state) => state.themeMode);
  const themeWithLocale = useMemo(() => {
    const theme = createAppTheme(themeMode);
    return createTheme(theme, locales[currentLanguage]);
  }, [currentLanguage, themeMode]);

  useEffect(() => {
    console.log('App component loaded');
    console.log('App组件已加载', {
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      currentPath: window.location.pathname,
      currentSearch: window.location.search,
      currentHash: window.location.hash
    });

    // 安全地更新页面标题
    const titleContainer = document.getElementById("title");
    if (titleContainer) {
      titleContainer.innerHTML = t("webTitle");
    }

    return () => {};
  }, [t]);

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={themeWithLocale}>
        <Routes>
          <Route path={ROUTES.index} element={<FIntro />} />
          <Route path={ROUTES.register} element={<NewRegister />} />
          <Route path={ROUTES.registerConfirm} element={<FRegisterConfirm />} />
          <Route path={ROUTES.registerError} element={<RegisterError />} />
          {/* 使用 BFF AuthRoute 替代 OidcSecure */}
          <Route
            path={ROUTES.home}
            element={
              <AuthRoute>
                <FLayout />
              </AuthRoute>
            }
          >
            <Route index element={<Navigate to={ROUTES_LIST.dashboard} replace />} />
            <Route path={ROUTES_LIST.dashboard} element={<SecureFDashboard />} />
          </Route>
          <Route path={ROUTES.portal} element={<SecurePortal />} />
          <Route path={ROUTES.other} element={<NotFound />} />
        </Routes>
      </ThemeProvider>
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  );
};

export default App;
